import { apiClient } from './apiClient';
import { authService } from './authService';
import type {
  Purchase,
  ListPurchasesParams,
  PurchasesListResponse,
  ExtendPurchaseRequest,
  ExtendPurchaseResponse,
  MarkUsedResponse,
  IssueCompPurchaseRequest,
} from '../types/purchase';

export const purchaseService = {
  /**
   * List business purchases with optional filters
   * @param businessProfileId - The business profile ID
   * @param params - Query parameters for filtering
   */
  async listBusinessPurchases(
    businessProfileId: string,
    params: ListPurchasesParams = {}
  ): Promise<PurchasesListResponse | Purchase[]> {
    const token = authService.storage.getToken();

    // Build query string from params
    const queryParams = new URLSearchParams();

    if (params.status) queryParams.append('status', params.status);
    if (params.expiryDateFrom) queryParams.append('expiryDateFrom', params.expiryDateFrom);
    if (params.expiryDateTo) queryParams.append('expiryDateTo', params.expiryDateTo);
    if (params.purchaseDateFrom) queryParams.append('purchaseDateFrom', params.purchaseDateFrom);
    if (params.purchaseDateTo) queryParams.append('purchaseDateTo', params.purchaseDateTo);
    if (params.customerEmail) queryParams.append('customerEmail', params.customerEmail);
    if (params.customerName) queryParams.append('customerName', params.customerName);
    if (params.productId) queryParams.append('productId', params.productId);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    const queryString = queryParams.toString();
    const endpoint = `/purchases/business/${businessProfileId}${queryString ? `?${queryString}` : ''}`;

    return apiClient.get<PurchasesListResponse>(endpoint, token || undefined);
  },

  /**
   * Extend purchase expiry date
   * @param purchaseId - The purchase ID to extend
   * @param data - New expiry date and optional notes
   */
  async extendPurchaseExpiry(
    purchaseId: string,
    data: ExtendPurchaseRequest
  ): Promise<ExtendPurchaseResponse> {
    const token = authService.storage.getToken();

    return apiClient.put<ExtendPurchaseResponse>(
      `/purchases/${purchaseId}/extend`,
      data,
      token || undefined
    );
  },

  async markPurchaseAsUsed(purchaseId: string): Promise<MarkUsedResponse> {
    const token = authService.storage.getToken();

    return apiClient.post<MarkUsedResponse>(
      `/purchases/${purchaseId}/mark-used`,
      {},
      token || undefined
    );
  },

  async deleteStalePending(purchaseId: string): Promise<void> {
    const token = authService.storage.getToken();
    return apiClient.delete<void>(`/purchases/${purchaseId}/pending`, token || undefined);
  },

  async issueComp(businessProfileId: string, req: IssueCompPurchaseRequest): Promise<Purchase> {
    const token = authService.storage.getToken();
    return apiClient.post<Purchase>(
      `/purchases/business/${businessProfileId}/comp`,
      req,
      token || undefined,
    );
  },
};
